import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';

import { getClickIdHeaders } from 'lib/api/clickIdHeaders';
import { parseError } from 'lib/api/errors/parseError';
import { attachStepUpInterceptor, attachStepUpMockInterceptor } from 'lib/api/stepUpVerification';
import { createSubaccountInterceptor } from 'lib/api/subaccount';
import { attachTradeGeoComplianceInterceptor } from 'lib/geocomply/tradeRequestInterceptor';
import type { AuthLogoutReason } from 'lib/stores/authStore';
import { useAuthStore } from 'lib/stores/authStore';
import * as datadogRum from 'lib/telemetry/datadogRum';
import { useBannerStore } from 'stores/zustand/bannerStore';
import { getSubaccountNumber } from 'stores/zustand/subAccountStore';
import logProviderFactory from 'utils/logger/logProviderFactory';

import { CSRF_HEADER_NAME, CSRF_LOCAL_STORAGE_KEY } from '../constants/session';
import { attachAwsWafTokenToAxiosConfig, maybeRetryAxiosRequestWithAwsWafToken } from '../lib/security/awsWaf';
import type { ApiErrorType, ServerError } from '../models';
import config from '../utils/config';
import { getLocalStorageWithExpiration, setLocalStorageWithExpiration } from '../utils/storage';
import RateLimiter from './ratelimiter';

export const noop = (): null => null;

// API error codes that represent expected user-facing conditions and should not
// reach Datadog as errors. Add codes here to suppress without touching callers.
export const SUPPRESSED_API_ERROR_CODES = new Set([
    'insufficient_balance',
    'invalid_credentials',
    // Expected wrong/expired verification-code entry (pre-signup email verify,
    // password reset) — handled inline by the UI, same as the old flow's 404.
    'invalid_or_expired_code',
    // Expected control flow: gated phone change starts with this 409, then the UI
    // collects the update-phone code.
    'phone_change_mfa_gate_required',
    // Same shape for authenticator enrollment: 409 first, then the UI collects
    // the update-mfa code.
    'authenticator_enrollment_mfa_gate_required',
    'trading_limit_exceeded',
]);

export const exchangeApi = axios.create({
    baseURL: `${config.exchangeApiUrl}/v1`,
    withCredentials: true,
    xsrfHeaderName: CSRF_HEADER_NAME,
    headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
    },
    timeout: config.requestTimeoutMs,
});

export const fredAPI = axios.create({
    headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
    },
    timeout: config.requestTimeoutMs,
});

export const bffApi = axios.create({
    baseURL: `${config.exchangeApiUrl}/v1/bff`,
    withCredentials: true,
    xsrfHeaderName: CSRF_HEADER_NAME,
    headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
    },
    timeout: config.requestTimeoutMs,
});

export const rateLimiter = new RateLimiter();

export function getCsrfToken(): string {
    return getLocalStorageWithExpiration(CSRF_LOCAL_STORAGE_KEY);
}

export function setCsrfToken(token: string): void {
    setLocalStorageWithExpiration(CSRF_LOCAL_STORAGE_KEY, token);
}

export function clearCsrfToken(): void {
    localStorage.removeItem(CSRF_LOCAL_STORAGE_KEY);
}

// Now that we have the rateLimiter this is required so that we don't logout
// Before some of the stacked requests are started
// TODO: Try an alternative approach that clears the pending requests in the rate limiter queue by using cancelation.
// that clear the rate limiter queue, for example handling the cancellation token
let logoutLock = false;

export function isLogoutLocked(): boolean {
    return logoutLock;
}

export function setLogoutLock(value: boolean): void {
    logoutLock = value;
}

// Each key in this set represents a "unique" request made. This set is used to check whether a request has already been made.
const sentRequestIdentifiers: Set<string> = new Set();

/*
  This function handle store changes we need to make depending on the request errors regardless of the components that started the request.
 */
function handleGlobalError(axiosError: AxiosError<ApiErrorType>): void {
    const isMaintenance = useBannerStore.getState().maintenanceMode === 'on';
    const parsedError = parseError(axiosError, { isMaintenance });
    if (parsedError.cancelled || parsedError.timeout || parsedError.notFound || isLogoutLocked()) {
        return;
    }

    const authActions = useAuthStore.getState().actions;
    const { isLoggedIn, userId } = useAuthStore.getState();

    // We guarantee on the back-end that unauthorized is returned only when it is not possible to proceed with the current session.
    if (parsedError.unauthorized) {
        // This means the user session is corrupted — one of the headers / cookies
        // could have been erased / expired. Log out whenever a userId is still
        // present, even if the access level was already lost (a corrupted partial
        // session), so we don't keep sending authenticated requests with a stale userId.
        if (userId) {
            authActions.logout('session_expired');
        } else {
            // No userId to log out, but access level and sub-user id/scopes are
            // read independently of userId (see _initializeFromCookie) and may
            // still be stale/corrupted in memory — clear them without touching
            // logoutReason (would otherwise re-suppress errors after an unrelated
            // manual sign-out — see handleErrorCallback below).
            authActions.clearAccessLevel();
            authActions.clearSubUser();
            authActions.clearPersistedSession();
        }
        // Registration-local state cleanup is handled reactively by
        // lib/stores/sessionTeardownEffects.ts, which subscribes to this token bump.
        authActions.notifyUnauthorizedResponse();
    } else if (isLoggedIn && parsedError.unavailable) {
        if (parsedError.maintenance) {
            authActions.logout('maintenance');
        } else if (parsedError?.error?.code === 'auth_service_unavailable') {
            authActions.logout('unavailability');
        }
    }
}

// 'manual' logouts (explicit sign-out) leave the user browsing public, logged-out
// pages — errors from those requests should still surface. Only the forced
// disconnect reasons should suppress in-flight error callbacks. Mirrors
// isForcedDisconnectReason in lib/stores/authStore.ts; kept local so this module
// works under tests that mock authStore with a minimal factory.
function isForcedDisconnectReason(
    reason: AuthLogoutReason,
): reason is 'session_expired' | 'maintenance' | 'unavailability' {
    return reason !== 'manual';
}

/*
  This function decides which errors should be repassed to the store / views layer.
 */
export function handleErrorCallback(
    error: AxiosError<ApiErrorType>,
    onErr: (err: ServerError) => void,
    onLog?: (parsedError: ServerError) => void,
): void {
    // After a forced disconnect (session expiry, maintenance, backend
    // unavailability), in-flight requests may fail but their error callbacks
    // would show stale toasts to a logged-out user. A manual sign-out is not
    // suppressed here — the user keeps browsing public pages afterward and
    // should still see errors from those requests.
    const logoutReason = useAuthStore.getState().logoutReason;
    if (logoutReason !== null && isForcedDisconnectReason(logoutReason)) {
        return;
    }

    const isMaintenance = useBannerStore.getState().maintenanceMode === 'on';
    const parsedError = parseError(error, { isMaintenance });

    if (onLog) {
        onLog(parsedError);
    } else if (error.response) {
        const apiError = error.response.data?.error;
        const errorMessage = apiError?.message ?? error.message;
        const isSuppressed = SUPPRESSED_API_ERROR_CODES.has(apiError?.code ?? '');
        if (
            parsedError.notFound ||
            parsedError.cancelled ||
            parsedError.tooManyRequests ||
            parsedError.timeout ||
            isSuppressed
        ) {
            // Log as info
            logProviderFactory.logMessage(`Transport layer info: ${errorMessage}`);
        } else {
            // Log as errors all errors except not found, cancelled requests, too many requests and timeouts
            const errorToLog = {
                ...error,
                message: `Transport layer error: ${errorMessage}`,
            };
            logProviderFactory.logError(errorToLog, error.request.responseURL);
        }
    }

    onErr(parsedError);
}

bffApi.interceptors.request.use(async (requestConfig) => {
    const csrfToken = getCsrfToken();
    if (!!csrfToken) {
        requestConfig.headers[CSRF_HEADER_NAME] = csrfToken;
    }
    return attachAwsWafTokenToAxiosConfig(requestConfig);
});

exchangeApi.interceptors.request.use(async (requestConfig) => {
    const csrfToken = getCsrfToken();
    if (!!csrfToken) {
        requestConfig.headers[CSRF_HEADER_NAME] = csrfToken;
    }

    // If the data is FormData, remove the Content-Type header to let the browser set it
    if (requestConfig.data instanceof FormData) {
        delete requestConfig.headers['Content-Type'];
    }

    /*
     * When the tab running this app is not visible (per the Page Visibility API),
     * all *repeat* requests to the exchange are intercepted and discarded.
     * If the browser is old and does not support this API, then no request is blocked.
     *
     * The special `allowRequestEvenIfPageIsHidden` param can be used to disable this behavior for a specific request.
     */
    const { allowRequestEvenIfPageIsHidden, ...params } = requestConfig.params ?? {};
    requestConfig.params = params;
    const requestIdentifier =
        '' +
        requestConfig.method +
        requestConfig.url +
        JSON.stringify(requestConfig.params) +
        JSON.stringify(requestConfig.data);

    if (typeof window !== 'undefined') {
        if (sentRequestIdentifiers.has(requestIdentifier) && !allowRequestEvenIfPageIsHidden && document.hidden) {
            return {
                ...requestConfig,
                cancelToken: new axios.CancelToken((cancel) => cancel('Cancel request due to page not being visible.')),
            };
        }
    }
    sentRequestIdentifiers.add(requestIdentifier);

    const clickIdHeaders = getClickIdHeaders(requestConfig);
    Object.entries(clickIdHeaders).forEach(([key, value]) => requestConfig.headers.set(key, value));

    return attachAwsWafTokenToAxiosConfig(requestConfig);
});

exchangeApi.interceptors.request.use((request) => {
    if (
        (request.url?.includes('/users//') || request.url?.endsWith('/users/')) &&
        request.method?.toLowerCase() !== 'post'
    ) {
        throw new axios.Cancel('Request cancelled due to missing userId');
    }

    return request;
});

const deduplicateContentType = (requestConfig: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const ct = requestConfig.headers?.['Content-Type'];
    if (typeof ct === 'string' && ct.includes(',')) {
        const deduped = ct.split(',')[0].trim();
        requestConfig.headers['Content-Type'] = deduped;
        try {
            datadogRum.addError(new Error('Duplicated Content-Type header'), {
                source: 'content-type-dedup',
                original_value: ct,
                resolved_value: deduped,
                url: requestConfig.url,
                method: requestConfig.method,
            });
        } catch {
            // Datadog may not be initialized yet
        }
    }
    return requestConfig;
};
exchangeApi.interceptors.request.use(deduplicateContentType);
fredAPI.interceptors.request.use(deduplicateContentType);
bffApi.interceptors.request.use(deduplicateContentType);
// Registered last so it runs first (request interceptors are LIFO) —
// downstream interceptors (hashing for dedup, AWS WAF signatures) then
// see the final URL/params/body with subaccount_number attached.
exchangeApi.interceptors.request.use(createSubaccountInterceptor(getSubaccountNumber));
if (process.env.NEXT_PUBLIC_FORCE_STEP_UP_MOCK === 'true') {
    attachStepUpMockInterceptor(exchangeApi);
}
exchangeApi.interceptors.response.use(
    (response) => {
        // Custom handling of the response in case of success
        if (!!response?.data?.error) {
            const axiosError: AxiosError<AxiosResponse> = {
                config: response.config,
                response,
                isAxiosError: false,
                message: 'Business error',
                name: 'ApiBusinessError',
                toJSON: () => response.data,
            };
            return Promise.reject(axiosError);
        }

        // Store CSRF Token if exists
        const csrfToken = response.headers[CSRF_HEADER_NAME];
        if (!!csrfToken) {
            setCsrfToken(csrfToken);
        }

        return response;
    },
    (error) => Promise.reject(error),
);
exchangeApi.interceptors.response.use(
    (response) => response,
    (error) => maybeRetryAxiosRequestWithAwsWafToken(exchangeApi, error),
);
exchangeApi.interceptors.response.use(
    (response) => response,
    (error: AxiosError<ApiErrorType>) => {
        // The reason why this is done here is because we don't wanna risk specific requests methods missing calls for the globalErrorHandler
        handleGlobalError(error);
        return Promise.reject(error);
    },
);
attachStepUpInterceptor(exchangeApi);
attachTradeGeoComplianceInterceptor(exchangeApi);
bffApi.interceptors.response.use(
    (response) => response,
    (error) => maybeRetryAxiosRequestWithAwsWafToken(bffApi, error),
);
